// pages/public/CaptureForm.jsx
// Step 1: User selects Individual or Company then fills in details
// Individual: name, email, job title, company name
// Company: name, email, company name, company code

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAssessment } from '../../context/AssessmentContext';
import axios from 'axios';

const CaptureForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { captureUser, loading } = useAssessment();

  // Read passcode from URL query param ?code=EXS-PLX-4872
  // OR from location state (when coming from landing page)
  const urlParams = new URLSearchParams(location.search);
  const urlPasscode = urlParams.get('code') || '';
  const prefilledPasscode = urlPasscode || location.state?.passcode || '';

  const [userType, setUserType] = useState(prefilledPasscode ? 'company' : null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    companyName: '',
    companyCode: prefilledPasscode,
  });
  const [error, setError] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const [validatedCompany, setValidatedCompany] = useState(null);

  // Auto validate passcode if it came from URL
  useEffect(() => {
    if (prefilledPasscode && userType === 'company') {
      handleValidateCode(prefilledPasscode);
    }
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    if (e.target.name === 'companyCode') setValidatedCompany(null);
  };

  const handleValidateCode = async (codeOverride) => {
    const code = codeOverride || form.companyCode;
    if (!code.trim()) {
      setError('Please enter your company code');
      return;
    }
    setValidatingCode(true);
    setError('');
    try {
      const response = await axios.post('/assessment/validate-passcode', {
        passcode: code.trim().toUpperCase(),
      });
      if (response.data.success) {
        setValidatedCompany(response.data.data);
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid company code. Please check and try again.');
      setValidatedCompany(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError('Please fill in your first name, last name and email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (userType === 'company' && !validatedCompany) {
      setError('Please validate your company code before continuing');
      return;
    }

    const result = await captureUser({
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      jobTitle: form.jobTitle,
      companyName: userType === 'company' ? validatedCompany?.companyName : form.companyName,
      passcodeUsed: userType === 'company' ? form.companyCode.toUpperCase() : undefined,
      companyId: userType === 'company' ? validatedCompany?.companyId : undefined,
    });

    if (result?.success) {
      // All users go straight to assessment now
      // Individual users pay AFTER assessment on free report page
      navigate('/assessment');
    } else {
      setError(result?.error || 'Something went wrong. Please try again.');
    }
  };

  // Type selection screen
  if (!userType) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={styles.logo} onClick={() => navigate('/')}>
            <span style={styles.logoExec}>EXECUTIVE</span>
            <span style={styles.logoSpring}>Springboard</span>
          </div>
        </div>
        <div style={styles.container} className="esb-container">
          <div style={styles.typeCard}>
            <h1 style={styles.typeTitle}>How are you taking this assessment?</h1>
            <p style={styles.typeSubtitle}>Choose the option that applies to you</p>
            <div style={styles.typeOptions} className="esb-grid-2">
              <button style={styles.typeOption} onClick={() => setUserType('individual')}>
                <div style={styles.typeOptionIcon}>👤</div>
                <div style={styles.typeOptionLabel}>Individual</div>
                <div style={styles.typeOptionDesc}>
                  I am taking this assessment on my own and will pay $149 for my detailed report
                </div>
              </button>
              <button style={styles.typeOption} onClick={() => setUserType('company')}>
                <div style={styles.typeOptionIcon}>🏢</div>
                <div style={styles.typeOptionLabel}>Through My Company</div>
                <div style={styles.typeOptionDesc}>
                  My company has enrolled me and I have a company code to enter
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.logo} onClick={() => navigate('/')}>
          <span style={styles.logoExec}>EXECUTIVE</span>
          <span style={styles.logoSpring}>Springboard</span>
        </div>
      </div>

      <div style={styles.container} className="esb-container">
        <div style={styles.progressWrap}>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: '33%' }} />
          </div>
          <span style={styles.progressText}>Step 1 of 3</span>
        </div>

        {/* Type badge */}
        <div style={styles.typeBadge}>
          <span style={styles.typeBadgeIcon}>{userType === 'individual' ? '👤' : '🏢'}</span>
          <span style={styles.typeBadgeText}>{userType === 'individual' ? 'Individual' : 'Company Assessment'}</span>
          <button style={styles.switchType} onClick={() => { setUserType(null); setValidatedCompany(null); setError(''); }}>
            Change
          </button>
        </div>

        <div style={styles.card} className="esb-card-lg">
          <h1 style={styles.title}>Let us get started</h1>
          <p style={styles.subtitle}>
            {userType === 'individual'
              ? 'Enter your details to begin your leadership assessment.'
              : 'Enter your details and company code to access your company assessment.'
            }
          </p>

          {error && <div style={styles.errorBox}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div style={styles.row} className="esb-grid-2">
              <div style={styles.formGroup}>
                <label style={styles.label}>First Name *</label>
                <input name="firstName" type="text" style={styles.input} placeholder="Steve" value={form.firstName} onChange={handleChange} disabled={loading} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Last Name *</label>
                <input name="lastName" type="text" style={styles.input} placeholder="Moss" value={form.lastName} onChange={handleChange} disabled={loading} />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email Address *</label>
              <input name="email" type="email" style={styles.input} placeholder="steve@company.com" value={form.email} onChange={handleChange} disabled={loading} />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Job Title <span style={styles.optional}>(optional)</span></label>
              <input name="jobTitle" type="text" style={styles.input} placeholder="VP Operations" value={form.jobTitle} onChange={handleChange} disabled={loading} />
            </div>

            {userType === 'individual' ? (
              <div style={styles.formGroup}>
                <label style={styles.label}>Company <span style={styles.optional}>(optional)</span></label>
                <input name="companyName" type="text" style={styles.input} placeholder="Acme Corp" value={form.companyName} onChange={handleChange} disabled={loading} />
              </div>
            ) : (
              <div style={styles.formGroup}>
                <label style={styles.label}>Company Code *</label>
                <div style={styles.codeRow}>
                  <input
                    name="companyCode"
                    type="text"
                    style={{ ...styles.input, textTransform: 'uppercase', letterSpacing: '2px', fontWeight: '700', flex: 1 }}
                    placeholder="EXS-ABC-1234"
                    value={form.companyCode}
                    onChange={handleChange}
                    disabled={loading || validatingCode}
                  />
                  <button
                    type="button"
                    style={styles.btnValidate}
                    onClick={handleValidateCode}
                    disabled={validatingCode || !form.companyCode.trim()}
                  >
                    {validatingCode ? 'Checking...' : 'Validate'}
                  </button>
                </div>
                {validatedCompany && (
                  <div style={styles.validatedBox}>
                    ✅ Code validated for <strong>{validatedCompany.companyName}</strong>
                  </div>
                )}
                <p style={styles.codeNote}>Your company code was provided by your HR or program coordinator.</p>
              </div>
            )}

            <button
              type="submit"
              style={{
                ...styles.btnSubmit,
                ...(userType === 'company' && !validatedCompany ? styles.btnDisabled : {}),
              }}
              disabled={loading || (userType === 'company' && !validatedCompany)}
            >
              {loading ? 'Saving...' : 'Continue to Assessment →'}
            </button>
          </form>

          <p style={styles.privacy}>
            Your information is kept confidential and will never be shared without your consent.
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', backgroundColor: 'var(--color-off-white)', fontFamily: 'var(--font-primary)' },
  header: { backgroundColor: 'var(--color-primary)', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center' },
  logo: { display: 'flex', flexDirection: 'column', gap: '1px', cursor: 'pointer' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#FFFFFF', lineHeight: 1 },
  container: { maxWidth: '580px', margin: '0 auto', padding: '40px 24px' },

  // Type selection
  typeCard: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)', padding: '40px' },
  typeTitle: { fontSize: '24px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '8px', textAlign: 'center' },
  typeSubtitle: { fontSize: '15px', color: 'var(--color-text-light)', marginBottom: '32px', textAlign: 'center' },
  typeOptions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  typeOption: { backgroundColor: '#fff', border: '2px solid var(--color-border)', borderRadius: '12px', padding: '24px 20px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' },
  typeOptionIcon: { fontSize: '36px', marginBottom: '12px' },
  typeOptionLabel: { fontSize: '16px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '8px' },
  typeOptionDesc: { fontSize: '13px', color: 'var(--color-text-light)', lineHeight: '1.5' },

  // Form
  progressWrap: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  progressBar: { flex: 1, height: '6px', backgroundColor: 'var(--color-light-gray)', borderRadius: '100px', overflow: 'hidden' },
  progressFill: { height: '100%', backgroundColor: 'var(--color-teal)', borderRadius: '100px' },
  progressText: { fontSize: '13px', color: 'var(--color-text-light)', whiteSpace: 'nowrap' },
  typeBadge: { display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'var(--color-info-bg)', border: '1px solid var(--color-teal)', borderRadius: '100px', padding: '6px 16px', marginBottom: '16px', width: 'fit-content' },
  typeBadgeIcon: { fontSize: '14px' },
  typeBadgeText: { fontSize: '13px', fontWeight: '600', color: 'var(--color-teal)' },
  switchType: { background: 'none', border: 'none', color: 'var(--color-teal)', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', padding: 0 },
  card: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)', padding: '40px' },
  title: { fontSize: '24px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '8px' },
  subtitle: { fontSize: '15px', color: 'var(--color-text-light)', lineHeight: '1.6', marginBottom: '24px' },
  errorBox: { background: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid #F5C6CB', borderRadius: '8px', padding: '12px 16px', fontSize: '14px', marginBottom: '20px' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formGroup: { marginBottom: '18px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--color-text)', marginBottom: '6px' },
  optional: { fontWeight: '400', color: 'var(--color-text-light)' },
  input: { width: '100%', padding: '12px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '15px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' },
  codeRow: { display: 'flex', gap: '10px', alignItems: 'center' },
  btnValidate: { backgroundColor: 'var(--color-primary)', color: '#fff', padding: '12px 16px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  validatedBox: { backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)', border: '1px solid #A9DFBF', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', marginTop: '8px' },
  codeNote: { fontSize: '12px', color: 'var(--color-text-light)', marginTop: '6px' },
  btnSubmit: { width: '100%', backgroundColor: 'var(--color-teal)', color: '#fff', padding: '14px', borderRadius: '8px', border: 'none', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginTop: '4px' },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  privacy: { fontSize: '12px', color: 'var(--color-text-light)', textAlign: 'center', marginTop: '16px' },
};

export default CaptureForm;
